import React from 'react';
import { Table, type TableProps } from 'antd';
import { IInbound, ISummaryCostByBUProduct } from '@/interfaces/inbound';
import TableComponent from '@/components/table';
import { formatNumber } from '@/utils/dataUtils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { round } from 'lodash';
interface IProps {
  items: ISummaryCostByBUProduct[];
  inbound?: IInbound;
}

const CostCalculationTable = ({ items, inbound }: IProps) => {
  dayjs.extend(relativeTime);
  const columns: TableProps<ISummaryCostByBUProduct>['columns'] = [
    {
      title: 'BU',
      dataIndex: 'businessUnitName',
      key: 'businessUnitName',
      align: 'center',
      width: 60,
      className: 'font-bold',
    },
    {
      title: 'ITEM CODE',
      dataIndex: 'productCode',
      key: 'productCode',
      className: 'font-bold text-blue-700',
      align: 'center',
      width: 130,
    },
    {
      title: 'ITEM NAME',
      dataIndex: 'productName',
      key: 'productName',
      className: 'font-bold',
    },
    {
      title: 'QTY',
      dataIndex: 'quantity',
      key: 'quantity',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: (_, rec) => formatNumber(rec.totalQty, 2),
    },
    {
      title: 'FOREIGN AMT',
      dataIndex: 'unitCost',
      key: 'unitCost',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: (_, rec) => formatNumber(rec.totalCost, 2),
    },
    {
      title: 'THB',
      dataIndex: 'unitCostTHB',
      key: 'unitCostTHB',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: (_, rec) => formatNumber(rec.totalUnitCostTHB, 2),
    },
    {
      title: 'DUTY FEE',
      dataIndex: 'duty',
      key: 'duty',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: (_, rec) => formatNumber(rec.totalDuty, 2),
    },
    {
      title: 'VAT',
      dataIndex: 'vatCost',
      key: 'vatCost',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: (_, rec) => formatNumber(rec.totalVatCost, 2),
    },
    {
      title: 'FREIGHT',
      dataIndex: 'freight',
      key: 'freight',
      className: 'font-bold',
      width: 100,
      align: 'right',
      render: (_, rec) => formatNumber(rec.totalFreight, 2),
    },
    {
      title: 'TOTAL COST',
      dataIndex: 'freight',
      key: 'freight',
      className: 'font-bold text-green-700',
      width: 100,
      align: 'right',
      render: (_, rec) => formatNumber(rec.summaryCost, 2),
    },
    {
      title: 'PER UNIT',
      dataIndex: 'costPerUnit',
      key: 'costPerUnit',
      className: 'font-bold text-blue-700',
      width: 100,
      align: 'right',
      render: (_, rec) => formatNumber(rec.costPerUnit, 2),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<ISummaryCostByBUProduct>
        columns={columns}
        criteria={undefined}
        data={{ data: items || [], totalRecords: items?.length }}
        loading={false}
        onTableChange={undefined}
        rowKey="id"
        // scrollLength={1800}
        scroll
        showPagination={false}
        summary={(pageData: ISummaryCostByBUProduct[]) => {
          let Quantity = 0;
          let Freight = 0;
          let Duty = 0;
          let UnitCost = 0;
          let UnitCostTHB = 0;
          let Vat = 0;
          let Cost = 0;
          pageData.forEach(
            ({
              totalQty,
              totalDuty,
              totalCost,
              summaryCost,
              totalVatCost,
              totalFreight,
              totalUnitCostTHB,
            }) => {
              Quantity += totalQty;
              Freight += round(totalFreight, 4);
              UnitCost += round(totalCost, 4);
              UnitCostTHB += round(totalUnitCostTHB, 4);
              Duty += round(totalDuty, 4);
              Vat += round(totalVatCost, 4);
              Cost += round(summaryCost, 4);
            },
          );

          const diffTotalCost = round((inbound?.totalCost || 0) - Cost, 4);
          const diffTotalFreight = round((inbound?.freight || 0) - Freight, 4);
          const diffTotalVat = round((inbound?.vatCost || 0) - Vat, 4);
          const diffTotalDuty = round((inbound?.dutyCost || 0) - Duty, 4);
          const diffTotalTHB = round(
            (inbound?.costOfGood || 0) * (inbound?.rate || 0) - UnitCostTHB,
            4,
          );
          const diffTotalUSD = round((inbound?.costOfGood || 0) - UnitCost, 4);

          return (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell
                  index={0}
                  colSpan={3}
                  className="font-bold items-end"
                  align="right"
                ></Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(Quantity, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(UnitCost, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(UnitCostTHB, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(Duty, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(Vat, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(Freight, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                >
                  {formatNumber(Cost, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-red-100"
                  align="right"
                ></Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell
                  index={0}
                  colSpan={3}
                  className="font-bold items-end"
                  align="right"
                >
                  DIFF
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                ></Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                >
                  {formatNumber(diffTotalUSD, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                >
                  {formatNumber(diffTotalTHB, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                >
                  {formatNumber(diffTotalDuty, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                >
                  {formatNumber(diffTotalVat, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                >
                  {formatNumber(diffTotalFreight, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                >
                  {formatNumber(diffTotalCost, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                ></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
    </div>
  );
};

export default CostCalculationTable;
